import { useState, useContext, useEffect, Component, CSSProperties } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { saveAs } from 'file-saver';
import * as Message from '../common/js/message';
import { Request } from '../common/js/request';
import { Context, AppContext } from "../common/js/context";
import { T, COMMON, Lang, getDefaultLang } from '../common/js/i18n';
import { getUserState, setLogined } from '../store/index';
import iconXmark from '../assets/icon/xmark.svg';
import iconPenToSquare from '../assets/icon/pen-to-square.svg';
import iconBars from '../assets/icon/bars.svg';
import iconShareFromSquare from '../assets/icon/share-from-square.svg';
import ModalDialog from '../components/ModalDialog';
import LangSwitch from "./fragment/LangSwitch";
import Edit from './Edit';
import styles from './Dashboard.module.css';
import CodeGenHelp from "./help/CodeGenHelp";
import PublicNavigator from "./share/PublicNavigator";

interface Scheme {
    id: string;
    name: string;
    open: boolean;
    created_time: string;
    last_modified_time: string;
}

interface SchemeProps {
    onSelect: (schemeId?: string) => void;
}

enum SchemeStatus {
    Normal,
    NormalWithExpandedAction,
    Editing,
}
interface SchemeWrapper {
    status: SchemeStatus;
    scheme: Scheme;
}

interface SchemeState {
    activeSchemeId?: string;
    schemeList: SchemeWrapper[];
    addName: string;
    adding: boolean;
    hoverIndex: number;
    activeDeleteId?: string;
    activeShareId?: string;
}

class SchemeList extends Component<SchemeProps, SchemeState> {
    static contextType = Context;
    constructor(props: SchemeProps) {
        super(props);
        this.state = {
            activeSchemeId: undefined,
            schemeList: [],
            addName: "",
            adding: false,
            hoverIndex: -1,
            activeDeleteId: undefined,
            activeShareId: undefined,
        };
    }
    async componentDidMount() {
        this.queryScheme();
    }
    async queryScheme() {
        let schemeList: Scheme[] = await new Request<{}, Scheme[]>("/api/scheme/queryScheme").call({});
        this.setState({
            schemeList: schemeList.map((scheme) => {
                return {
                    status: SchemeStatus.Normal,
                    scheme: scheme,
                };
            })
        });
    }
    async saveScheme(name: string, id?: string) {
        const context: AppContext = this.context as any;
        if (name) {
            await new Request("/api/scheme/saveScheme").call({
                id: id,
                name: name,
            });
            Message.success(id ? COMMON.UPDATE_SUCCESSFULLY[context.lang] : COMMON.ADD_SUCCESSFULLY[context.lang]);
            this.queryScheme();
        }
    }
    async deleteScheme(id: string) {
        const context: AppContext = this.context as any;
        await new Request("/api/scheme/deleteScheme").call({
            id: id,
        });
        Message.success(COMMON.DELETE_SUCCESSFULLY[context.lang]);
        if (this.state.activeSchemeId === id) {
            this.setState({
                activeSchemeId: undefined
            });
            this.props.onSelect(undefined);
        }
        this.queryScheme();
    }
    cloneSchemes(): SchemeWrapper[] {
        return this.state.schemeList.map(({ status, scheme }) => {
            return {
                status: status,
                scheme: {
                    id: scheme.id,
                    name: scheme.name,
                    open: scheme.open,
                    created_time: scheme.created_time,
                    last_modified_time: scheme.last_modified_time,
                }
            }
        });
    }
    async shareScheme(id: string) {
        let schemeList = this.cloneSchemes();
        for (let item of schemeList) {
            item.status = SchemeStatus.Normal;
            if (id === item.scheme.id) {
                if (!item.scheme.open) {
                    await new Request("/api/scheme/setOpen").call({
                        id: id,
                        open: true
                    });
                    item.scheme.open = true;
                }
            }
        }
        this.setState({
            schemeList: schemeList,
            activeShareId: id
        });
    }
    pkgShareUrl(id: string): string {
        return window.location.protocol + "//" + window.location.host + window.location.pathname + "#/open/" + id;
    }
    editing(): boolean {
        return this.state.schemeList.some((item) => SchemeStatus.Editing === item.status);
    }
    render() {
        const context: AppContext = this.context as any;
        return (
            <div>
                <div style={{ borderBottom: "1px solid #CCC", padding: "0.25em 0.5em", display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: "bold" }}>{T({ en: "Scheme(s)", zh: "方案列表" })}</span>
                    <div>
                        <button type="button" className="btn" disabled={this.state.adding || this.editing()} onClick={() => {
                            this.setState({
                                adding: true
                            });
                        }}>{T(COMMON.ADD)}</button>
                    </div>
                </div>
                <ul style={{
                    margin: 0,
                    padding: 0,
                    listStyleType: "none",
                }}>
                    {
                        this.state.adding &&
                        (
                            <li style={{ padding: "0.25em 0.5em" }}>
                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <span style={{ flexGrow: 0 }}>{T({ en: "Scheme Name", zh: "方案名称" })}:</span>
                                    <input style={{ flexGrow: 1, width: "1em" }} type="text" value={this.state.addName} onInput={(evt) => {
                                        this.setState({
                                            addName: (evt.target as any).value
                                        });
                                    }} />
                                    <span style={{ flexGrow: 0, paddingLeft: "0.25em" }}>
                                        <button type="button" className="btn" onClick={async (evt) => {
                                            await this.saveScheme(this.state.addName);
                                            this.setState({
                                                adding: false
                                            });
                                            evt.stopPropagation();
                                        }}>{T(COMMON.OK)}</button>
                                        <button type="button" className="btn" style={{ marginLeft: "0.25em" }} onClick={async (evt) => {
                                            this.setState({
                                                adding: false
                                            });
                                            evt.stopPropagation();
                                        }}>{T(COMMON.CANCEL)}</button>
                                    </span>
                                </div>
                            </li>
                        )
                    }
                    {
                        this.state.schemeList.map((item, index) => {
                            let iconsActive = index === this.state.hoverIndex && item.scheme.id === this.state.activeSchemeId && !this.state.adding && !this.editing();
                            let style: CSSProperties = {};
                            if (this.state.activeSchemeId === item.scheme.id && SchemeStatus.Editing !== item.status) {
                                style.backgroundColor = "#EEE";
                            }
                            return (
                                <li key={item.scheme.id} style={{ cursor: "default", padding: "0.25em 0.5em", ...style }} onClick={() => {
                                    this.setState({
                                        activeSchemeId: item.scheme.id
                                    });
                                    this.props.onSelect(item.scheme.id);
                                }} onMouseMove={() => {
                                    this.setState({
                                        hoverIndex: index
                                    });
                                }} onMouseLeave={() => {
                                    this.setState({
                                        hoverIndex: -1
                                    });
                                }}>
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        {
                                            SchemeStatus.Editing === item.status && [
                                                <input type="text" style={{ flexGrow: 1 }} value={item.scheme.name} onInput={(evt) => {
                                                    let schemeList = this.cloneSchemes();
                                                    schemeList[index].scheme.name = (evt.target as any).value;
                                                    this.setState({
                                                        schemeList: schemeList
                                                    });
                                                }} />,
                                                <span style={{ flexGrow: 0, paddingLeft: "0.25em" }}>
                                                    <button type="button" className="btn" style={{ flexGrow: 0 }} onClick={async (evt) => {
                                                        await this.saveScheme(item.scheme.name, item.scheme.id);
                                                        evt.stopPropagation();
                                                    }}>{T(COMMON.OK)}</button>
                                                    <button type="button" className="btn" style={{ marginLeft: "0.25em" }} onClick={async (evt) => {
                                                        let schemeList = this.cloneSchemes();
                                                        schemeList[index].status = SchemeStatus.Normal;
                                                        this.setState({
                                                            schemeList: schemeList
                                                        });
                                                        evt.stopPropagation();
                                                    }}>{T(COMMON.CANCEL)}</button>
                                                </span>
                                            ]
                                        }
                                        {
                                            SchemeStatus.Editing !== item.status && [
                                                <span key={item.scheme.id} style={{ flexGrow: 1 }}>{item.scheme.name}</span>,
                                                iconsActive && <span style={{ flexGrow: 0, position: "relative" }}>
                                                    <img src={iconBars} alt="" style={{ height: "1em", verticalAlign: "middle", cursor: "pointer" }} onClick={(evt) => {
                                                        let schemeList = this.cloneSchemes();
                                                        for (let item of schemeList) {
                                                            item.status = SchemeStatus.Normal;
                                                        }
                                                        schemeList[index].status = SchemeStatus.NormalWithExpandedAction;
                                                        this.setState({
                                                            schemeList: schemeList
                                                        });
                                                        evt.stopPropagation();
                                                    }} />
                                                    {
                                                        SchemeStatus.NormalWithExpandedAction === item.status &&
                                                        <ul onMouseLeave={() => {
                                                            let schemeList = this.cloneSchemes();
                                                            schemeList[index].status = SchemeStatus.Normal;
                                                            this.setState({
                                                                schemeList: schemeList
                                                            });
                                                        }} style={{ position: "absolute", left: "100%", top: "0", width: "8em", zIndex: 999, margin: 0, padding: 0, listStyleType: "none", backgroundColor: "white", border: "1px solid #CCC" }}>
                                                            <li className={styles['action-item']} style={{ padding: "0.25em 0.5em", cursor: "pointer" }} onClick={(evt) => {
                                                                let schemeList = this.cloneSchemes();
                                                                schemeList[index].status = SchemeStatus.Editing;
                                                                this.setState({
                                                                    schemeList: schemeList
                                                                });
                                                                evt.stopPropagation();
                                                            }}>
                                                                <img src={iconPenToSquare} alt="" style={{ height: "1em", verticalAlign: "middle" }} />
                                                                <span style={{ marginLeft: "0.5em", verticalAlign: "middle" }}>{T(COMMON.MODIFY)}</span>
                                                            </li>
                                                            <li className={styles['action-item']} style={{ padding: "0.25em 0.5em", cursor: "pointer" }} onClick={(evt) => {
                                                                this.shareScheme(item.scheme.id);
                                                                evt.stopPropagation();
                                                            }}>
                                                                <img src={iconShareFromSquare} alt="" style={{ height: "1em", verticalAlign: "middle" }} />
                                                                <span style={{ marginLeft: "0.5em", verticalAlign: "middle" }}>{T({ en: "Share", zh: "分享" })}</span>
                                                            </li>
                                                            <li className={styles['action-item']} style={{ padding: "0.25em 0.5em", cursor: "pointer" }} onClick={(evt) => {
                                                                let schemeList = this.cloneSchemes();
                                                                for (let item of schemeList) {
                                                                    item.status = SchemeStatus.Normal;
                                                                }
                                                                this.setState({
                                                                    schemeList: schemeList,
                                                                    activeDeleteId: item.scheme.id
                                                                });
                                                                evt.stopPropagation();
                                                            }}>
                                                                <img src={iconXmark} alt="" style={{ height: "1em", verticalAlign: "middle" }} />
                                                                <span style={{ marginLeft: "0.5em", verticalAlign: "middle" }}>{T(COMMON.DELETE)}</span>
                                                            </li>
                                                        </ul>
                                                    }
                                                </span>
                                            ]
                                        }
                                    </div>
                                </li>
                            )
                        })
                    }
                </ul>
                {
                    this.state.activeDeleteId && (
                        <ModalDialog title={COMMON.CONFIRM_TITLE[context.lang]} closable={false}>
                            <div style={{ width: "16em", padding: "0.5em" }}>
                                <div>{T(COMMON.CONFIRM_DELETING)}</div>
                                <div style={{ textAlign: "center" }}>
                                    <button type="button" className="btn" onClick={() => {
                                        this.deleteScheme(this.state.activeDeleteId!);
                                        this.setState({
                                            activeDeleteId: undefined
                                        });
                                    }}>{T(COMMON.OK)}</button>
                                    <button type="button" className="btn" style={{ marginLeft: "0.5em" }} onClick={() => {
                                        this.setState({
                                            activeDeleteId: undefined
                                        });
                                    }}>{T(COMMON.CANCEL)}</button>
                                </div>
                            </div>
                        </ModalDialog>
                    )
                }
                {
                    this.state.activeShareId && (
                        <ModalDialog title={{ en: "Share", zh: "分享" }[context.lang]} closable={false}>
                            <div style={{ width: "36em", padding: "0.5em" }}>
                                <div>{T({ en: "Access Url", zh: "访问地址" })}:</div>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <div style={{ flexGrow: 1, flexShrink: 1, padding: "0.25em", border: "1px solid #CCC" }}>{this.pkgShareUrl(this.state.activeShareId)}</div>
                                    <div style={{ flexGrow: 0, flexShrink: 0, padding: "0.25em 0 0.25em 0.25em" }} >
                                        <button type="button" className="btn" data-clipboard-text={this.pkgShareUrl(this.state.activeShareId)}>{T({ en: "Copy to clipboard", zh: "复制到剪贴板" })}</button>
                                    </div>
                                </div>
                                <div style={{ textAlign: "center", marginTop: "0.5em" }}>
                                    <button type="button" className="btn" onClick={() => {
                                        this.setState({
                                            activeShareId: undefined
                                        });
                                    }}>{T(COMMON.OK)}</button>
                                </div>
                            </div>
                        </ModalDialog>
                    )
                }
            </div>
        );
    }
}

function downloadKeys() {
    let userState = getUserState();
    let keys = JSON.stringify(userState.keys);
    const blob = new Blob([keys]);
    saveAs(blob, "keys.json");
}

async function readDownloadKeysOperationCount(): Promise<number> {
    let count: any = await new Request("/api/operationstatistic/readOperationCount").disableLoading().call({
        module: "user",
        operation: "downloadKeys",
    });
    return count;
}

async function recordDownloadKeysOperation() {
    await new Request("/api/operationstatistic/recordOperation").disableLoading().call({
        module: "user",
        operation: "downloadKeys",
    });
}

export default function Dashboard() {
    const navigate = useNavigate();
    const context = useContext(Context);
    const userState = getUserState();
    const [helpActive, setHelpActive] = useState(false);
    const [publicActive, setPublicActive] = useState(false);
    const [activeSchemeId, setActiveSchemeId] = useState(undefined as string | undefined);
    const [downloadKeysGuideActive, setDownloadKeysGuideActive] = useState(false);
    useEffect(() => {
        if (userState.logined) {
            readDownloadKeysOperationCount().then((count) => {
                setDownloadKeysGuideActive(0 >= count);
            });
        }
    }, []);
    if (userState.logined) {
        let leftWidth = "20em";
        let topHeight = "2em";
        const logout = async function () {
            await new Request("/api/user/logout").call({});
            localStorage.removeItem("keyPair");
            setLogined(false);
            navigate("/");
        };
        let isNotZh = Lang.Zh !== getDefaultLang();
        const link = __webpack_public_path__ + 'client/codegen-client.exe';
        const isNotNative = !(window as any).__TAURI__;
        return (
            <div style={{ height: "100%", position: "relative" }}>
                <div style={{ height: topHeight, position: "absolute", top: 0, left: 0, right: 0, boxSizing: "border-box", borderBottom: "1px solid #CCC", display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: "0.5em" }}>
                    {
                        isNotZh && <LangSwitch />
                    }
                    <a href="mailto:398914521@qq.com" style={{ marginLeft: "0.5em" }}>{T(COMMON.FEEDBACK)}</a>
                    {
                        isNotNative && <a href={link} target="blank" style={{ marginLeft: "0.5em" }}>{T({ en: "Download dedicated client", zh: "下载专用客户端" })}</a>
                    }
                    <button type="button" className="btn" onClick={async () => {
                        setHelpActive(true);
                    }}>{T({ en: "Guide", zh: "使用帮助" })}</button>

                    <button type="button" className="btn" onClick={async () => {
                        setPublicActive(true);
                    }}>{T({ en: "Open shared scheme", zh: "查看共享方案" })}</button>
                    <button type="button" className="btn" onClick={async () => {
                        downloadKeys();
                        await recordDownloadKeysOperation();
                        setDownloadKeysGuideActive(false);
                    }} style={{ color: downloadKeysGuideActive ? "red" : "inherit" }}>{T({ en: "Export account file", zh: "导出账户文件" })}</button>
                    <button type="button" className="btn" onClick={logout} style={{ marginLeft: "0.5em" }}>{T(COMMON.LOGOUT)}</button>
                </div>
                <div style={{ position: "absolute", top: topHeight, bottom: 0, left: 0, right: 0 }}>
                    <div style={{ height: "100%", width: leftWidth, position: "absolute", top: 0, bottom: 0, left: 0, boxSizing: "border-box", borderRight: "1px solid #CCC" }}>
                        <SchemeList onSelect={setActiveSchemeId} />
                    </div>
                    <div style={{ height: "100%", position: "absolute", top: 0, bottom: 0, left: leftWidth, right: 0 }}>
                        {
                            activeSchemeId && (<Edit schemeId={activeSchemeId} />)
                        }
                    </div>
                </div>
                {
                    publicActive && (
                        <ModalDialog title={{ en: "Go to shared scheme", zh: "查看共享方案" }[context.lang]} closable={false}>
                            <PublicNavigator onClose={() => {
                                setPublicActive(false);
                            }} />
                        </ModalDialog>
                    )
                }
                {
                    helpActive && (
                        <ModalDialog title={{ en: "Guide", zh: "使用帮助" }[context.lang]} closable={false}>
                            <CodeGenHelp onClose={() => {
                                setHelpActive(false);
                            }} />
                        </ModalDialog>
                    )
                }
            </div>
        );
    } else {
        return (
            <Navigate to="/" />
        );
    }
}
